/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import dateMath from '@elastic/datemath';
import { htmlIdGenerator } from '../../../../services';
import { EuiFlexGroup, EuiFlexItem } from '../../../flex';
import { EuiForm, EuiFormRow, EuiSelect, EuiFieldNumber, EuiFieldText, EuiSwitch, EuiFormLabel } from '../../../form';
import { EuiSpacer } from '../../../spacer';
import { parseRelativeParts, toRelativeStringFromParts } from '../relative_utils';
import { EuiScreenReaderOnly } from '../../../accessibility';
import { EuiI18n } from '../../../i18n';

// eslint-disable-line import/named

import { INVALID_DATE } from '../date_modes';
import { EuiPopoverFooter } from '../../../popover';
export class EuiRelativeTab extends Component {
  state = {
    ...parseRelativeParts(this.props.value)
  };
  relativeDateInputNumberDescriptionId = htmlIdGenerator()();
  onCountChange = event => {
    const sanitizedValue = parseInt(event.target.value, 10);
    this.setState({
      count: isNaN(sanitizedValue) ? undefined : sanitizedValue
    }, this.handleChange);
  };
  onUnitChange = event => {
    this.setState({
      unit: event.target.value
    }, this.handleChange);
  };
  onRoundChange = event => {
    this.setState({
      round: event.target.checked
    }, this.handleChange);
  };
  handleChange = () => {
    const {
      count,
      round,
      roundUnit,
      unit
    } = this.state;
    const {
      onChange
    } = this.props;
    if (count === undefined || count < 0) {
      return;
    }
    const date = toRelativeStringFromParts({
      count,
      round,
      roundUnit,
      unit
    });
    onChange(date);
  };
  render() {
    const {
      relativeOptions,
      relativeRoundingLabels
    } = this.props.timeOptions;
    const {
      count,
      unit
    } = this.state;
    const invalidDate = this.props.value === INVALID_DATE;
    const invalidValue = count === undefined || count < 0;
    const isInvalid = invalidValue || invalidDate;
    const parsedValue = dateMath.parse(this.props.value, {
      roundUp: this.props.roundUp
    });
    const formattedValue = isInvalid || !parsedValue || !parsedValue.isValid() ? '' : parsedValue.locale(this.props.locale || 'en').format(this.props.dateFormat);
    const getErrorMessage = ({
      numberInputError,
      dateInputError
    }) => {
      if (invalidValue) return numberInputError;
      if (invalidDate) return dateInputError;
      return null;
    };
    return <>
        <EuiForm className="euiDatePopoverContent__padded">
          <EuiFlexGroup gutterSize="s" responsive={false}>
            <EuiFlexItem>
              <EuiI18n tokens={['euiRelativeTab.numberInputError', 'euiRelativeTab.numberInputLabel', 'euiRelativeTab.dateInputError']} defaults={['Must be >= 0', 'Time span amount', 'Must be a valid range']}>
                {([numberInputError, numberInputLabel, dateInputError]) => <EuiFormRow isInvalid={isInvalid} error={getErrorMessage({
                numberInputError,
                dateInputError
              })}>
                    <EuiFieldNumber compressed aria-label={numberInputLabel} aria-describedby={this.relativeDateInputNumberDescriptionId} data-test-subj={'superDatePickerRelativeDateInputNumber'} value={count} onChange={this.onCountChange} isInvalid={isInvalid} />
                  </EuiFormRow>}
              </EuiI18n>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiI18n token="euiRelativeTab.unitInputLabel" default="Relative time span">
                {unitInputLabel => <EuiSelect compressed aria-label={unitInputLabel} data-test-subj={'superDatePickerRelativeDateInputUnitSelector'} value={unit} options={relativeOptions} onChange={this.onUnitChange} />}
              </EuiI18n>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="s" />
          <EuiFieldText compressed value={formattedValue} readOnly prepend={<EuiFormLabel>{this.props.labelPrefix}</EuiFormLabel>} />
          <EuiScreenReaderOnly>
            <p id={this.relativeDateInputNumberDescriptionId}>
              <EuiI18n token="euiRelativeTab.fullDescription" default="The unit is changeable. Currently set to {unit}." values={{
              unit
            }} />
            </p>
          </EuiScreenReaderOnly>
        </EuiForm>
        <EuiPopoverFooter paddingSize="s">
          <EuiSwitch data-test-subj={'superDatePickerRelativeDateRoundSwitch'} label={relativeRoundingLabels[unit.substring(0, 1)]} checked={this.state.round} onChange={this.onRoundChange} />
        </EuiPopoverFooter>
      </>;
  }
}
EuiRelativeTab.propTypes = {
  dateFormat: PropTypes.string.isRequired,
  locale: PropTypes.any,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  roundUp: PropTypes.bool,
  position: PropTypes.oneOf(["start", "end"]).isRequired,
  labelPrefix: PropTypes.string.isRequired,
  timeOptions: PropTypes.shape({
    timeTenseOptions: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
    timeUnitsOptions: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
    relativeOptions: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.oneOf(["s", "m", "h", "d", "w", "M", "y"]).isRequired, PropTypes.oneOf(["s+", "m+", "h+", "d+", "w+", "M+", "y+"]).isRequired]).isRequired
    }).isRequired).isRequired,
    relativeRoundingLabels: PropTypes.any.isRequired,
    refreshUnitsOptions: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
    commonDurationRanges: PropTypes.arrayOf(PropTypes.shape({
      end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,
      label: PropTypes.string,
      start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired
    }).isRequired).isRequired
  }).isRequired
};
try {
  EuiRelativeTab.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
    description: '',
    displayName: 'EuiRelativeTab',
    methods: [],
    props: {
      dateFormat: {
        defaultValue: null,
        description: '',
        name: 'dateFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      locale: {
        defaultValue: null,
        description: '',
        name: 'locale',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: '(date: string, event?: React.SyntheticEvent<any>) => void'
        }
      },
      roundUp: {
        defaultValue: null,
        description: '',
        name: 'roundUp',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      position: {
        defaultValue: null,
        description: '',
        name: 'position',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: '"end" | "start"',
          value: [{
            value: '"end"'
          }, {
            value: '"start"'
          }]
        }
      },
      labelPrefix: {
        defaultValue: null,
        description: '',
        name: 'labelPrefix',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      timeOptions: {
        defaultValue: null,
        description: '',
        name: 'timeOptions',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx',
          name: 'EuiRelativeTabProps'
        }],
        required: true,
        type: {
          name: 'TimeOptions'
        }
      }
    },
    extendedInterfaces: ['EuiRelativeTabProps']
  };
} catch (e) {}